import { Container, Sprite } from 'pixi.js';
import type { PointData, IHitArea } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedValue } from '@softgames/localization';
import { PiggyBankOverlayLayout } from '../../../../layouts/piggybank/PiggyBankOverlayLayout';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';
import { PiggyBankProgressBarView } from './PiggyBankProgressBarView';
import { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import { waitForMs } from '../../../utils/async-utils';
import { animateCoinFlySequence } from '../../../game/views/common/animateCoinFlySequence';
import { CoinCounterView } from '../../../game/views/common/counters/CoinCounterView';
import MathUtils from '../../../utils/MathUtils';
import { turnIntoParticle } from '../../../libs/particles/updateParticle';
import { animateColorTo } from '../../../utils/pixi/animations/filter-animation-utils';
import type { Tween } from '../../../libs/TweenManager/Tween';
import type { FXParticleEmitterConfig } from '../../../libs/particles/FXParticleEmitterConfig';
import type { FXParticleEmitter } from '../../../libs/particles/FXParticleEmitter';
import rewardParticlesJSON from '../../../../assets/particles{ignore}/PiggyBank_Reward_Particles.json';
import coinStackParticlesJSON from '../../../../assets/particles{ignore}/PiggyBank_Overlay_Sparkles_Particles.json';
import type { PiggyBankState } from '../PiggyBankState';
import { applyLayoutState, revokeLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';
import { LoadingSpinnerView } from '../../../views/LoadingSpinnerView';
import { PiggyBankTier } from '../types';
import { PIGGY_BANK_TIER_CONFIG_MAP } from '../PiggyBankConstants';
import { getNextPiggyBankTier } from '../PiggyBankUtils';
import { setTextAsTimerCountdown } from '../../../utils/pixi/setTextAsTimerCountdown';

const sparkleEmitterPositions: PointData[] = [
  { x: -260, y: 120 },
  { x: 220, y: 120 },
];

export interface PiggyBankOverlayViewConfig {
  piggyBankState: PiggyBankState;
  levelNumber: number;
  coins: number;
  isLastChance: boolean;
  getPiggyBankPrice: () => Promise<string | null>;
}

export class PiggyBankOverlayView extends Container {
  public static readonly requiredLayouts = [PiggyBankOverlayLayout];

  public readonly layout = new PiggyBankOverlayLayout();

  private readonly particleLayer: FXParticleLayer;

  private readonly progressBar: PiggyBankProgressBarView;

  private readonly pieces: Sprite[];

  private readonly fillCoinInitialPos: PointData;

  private fillLoopTweens: Tween[] = [];

  private readonly sparkleEmitters: FXParticleEmitter[];

  public readonly onClaimPiggyBank = new Signal();

  public readonly onPlayClicked = new Signal();

  public readonly onCloseClicked = new Signal();

  private readonly claimButtonActiveHitArea?: IHitArea | null;

  private readonly initialState: PiggyBankState;

  private readonly coinCounter: CoinCounterView;

  private readonly cancelTimeUpdate: (() => void) | null = null;

  constructor(private readonly config: PiggyBankOverlayViewConfig) {
    super();

    const { piggyBankState, levelNumber } = config;

    this.initialState = piggyBankState;

    const isFull = this.initialState.coins === this.initialState.maxCoins;

    const {
      layout,
      layout: {
        blueBackgroundContainer,
        redBackgroundContainer,
        particleLayerSlot,
        playButton,
        playButtonLevelText,
        closeButton,
        claimButton,
        raysContainer,
        raysMask,
        percentageText,
        timerContainer,
        timerText,
        pigLayout,
        pigLayout: { fillCoin, fillCoinMask, pigPieces, pigFullSprite },
        nextTierContainer,
        nextTierLayout,
        nextTierLayout: { pigNormal: nextPiggyBankTier },
      },
    } = this;

    this.addChild(layout.root);

    const { currentTier } = config.piggyBankState;
    applyLayoutState(pigLayout, currentTier);

    if (currentTier !== PiggyBankTier.Legacy) {
      const nextTier = getNextPiggyBankTier(currentTier);
      if (nextTier !== currentTier) {
        applyLayoutState(nextTierLayout, nextTier);
        nextPiggyBankTier.props({
          visible: true,
        });
      } else {
        nextTierContainer.props({
          alpha: 0,
        });
      }
    }

    if (isFull) {
      pigFullSprite.props({
        visible: true,
      });
    }

    this.pieces = pigPieces.children.filter((piece) => piece) as Sprite[];

    percentageText.text = `${PIGGY_BANK_TIER_CONFIG_MAP[currentTier].uiValue}%`;

    this.particleLayer = new FXParticleLayer(true).props({
      parent: particleLayerSlot,
    });

    this.progressBar = new PiggyBankProgressBarView(layout);

    blueBackgroundContainer.props({
      visible: !config.isLastChance,
    });

    redBackgroundContainer.props({
      visible: config.isLastChance,
    });

    // Create internal coin counter for claim animation (pass null state since we manage it manually)
    this.coinCounter = new CoinCounterView(null, false).props({
      parent: this.layout.coinCounterSlot,
      alpha: 0,
    });
    this.coinCounter.updateAmount(this.config.coins);
    this.coinCounter.updateEnabled = false;

    playButton.root.onpointertap = (): void => {
      this.onPlayClicked.emit();
    };

    playButtonLevelText.text = getLocalizedString(`!{Level} ${getLocalizedValue(levelNumber)}`);

    this.cancelTimeUpdate?.();

    if (piggyBankState.endTime) {
      this.cancelTimeUpdate = setTextAsTimerCountdown(timerText, piggyBankState.endTime);
    } else {
      timerContainer.visible = false;
    }

    closeButton.onpointertap = (): void => {
      this.onCloseClicked.emit();
    };

    this.claimButtonActiveHitArea = claimButton.root.hitArea;

    fillCoin.props({
      mask: fillCoinMask,
      visible: false,
    });

    raysContainer.props({
      mask: raysMask,
    });

    this.fillCoinInitialPos = {
      x: fillCoin.x,
      y: fillCoin.y,
    };

    this.sparkleEmitters = sparkleEmitterPositions.map(({ x, y }) =>
      this.particleLayer.addParticleEmitter(x, y, coinStackParticlesJSON as FXParticleEmitterConfig),
    );

    claimButton.root.onpointertap = (): void => {
      this.onClaimPiggyBank.emit();
    };

    registerResizableObject(this);

    this.updateProgress(this.initialState.coins, false);

    void this.toggleClaimButton(false, isFull);
    if (!isFull) this.startFillLoopAnimation();
    this.fadeIn();
  }

  public updateProgress(coins: number, instant = false): void {
    const { progressBar, initialState } = this;
    const progressRatio = Math.min(coins / initialState.maxCoins, 1);
    const duration = !instant ? progressRatio * 1000 : 0;

    void progressBar.updateProgress(coins, initialState.maxCoins, duration).then(() => {
      if (instant) return;

      if (progressBar.isFull()) {
        void this.toggleClaimButton(true, true);

        const { coinsFull, claimButton } = this.layout;
        coinsFull.props({
          alpha: 0,
        });
        coinsFull.tweenTo({ alpha: 1 }, 300, 'quadIn', true);

        animateColorTo(claimButton.root, 0xffffff, 0, 0.8, 250, 'quadOut', 0, 0, true);

        claimButton.root.tweenTo({ scaleXY: 1.2 }, 500, 'quarticOut', true, 0, 0, true).onComplete.addOnce(() => {
          claimButton.root.tweenTo({ scaleXY: 1.035 }, 800, 'quadOut', true, 0, -1, true);
        });
      }
    });
  }

  private updateCoinsClaimedAmount(amount: number): void {
    const { coinsAmountText } = this.layout;

    coinsAmountText.text = `x${getLocalizedValue(amount)}`;
  }

  public async close(): Promise<void> {
    this.interactiveChildren = false;
    this.removeTweens(true);
    await this.fadeOut(() => {
      this.destroy();
    });
  }

  public startFillLoopAnimation(): void {
    if (this.destroyed) return;

    const {
      pigLayout: { pigContainer, fillCoin },
    } = this.layout;

    const { fillCoinInitialPos } = this;

    const coinTween = fillCoin.tweenTo({ x: -44, y: -45 }, 500, 'quadIn', false);

    const pigTween = pigContainer.tweenTo(
      { scaleY: 0.97, scaleX: 0.99, y: pigContainer.y + 15 },
      250,
      'quadOut',
      false,
      0,
      0,
      true,
    );

    fillCoin.props({ ...fillCoinInitialPos, visible: true });
    const alphaTween = fillCoin.tweenFrom({ alpha: 0 }, 150, 'quadOut', true);

    coinTween.chain(pigTween);
    coinTween.start();

    pigTween.onComplete.addOnce(async () => {
      await waitForMs(2000);
      fillCoin.props({ ...fillCoinInitialPos });
      this.startFillLoopAnimation();
    });

    this.fillLoopTweens = [coinTween, pigTween, alphaTween];
  }

  public stopFillLoopAnimation(): void {
    const {
      pigLayout: { fillCoin },
    } = this.layout;

    this.fillLoopTweens.forEach((tween) => tween.stop());
    this.fillLoopTweens = [];
    fillCoin.props({
      visible: false,
    });
  }

  public async runClaimSequence(coinReward: number): Promise<void> {
    const { layout, progressBar, coinCounter } = this;
    const {
      coinParticleSlot,
      coinSprite,
      pigLayout: { pigContainer, fillCoin },
      root,
    } = layout;

    this.stopFillLoopAnimation();

    progressBar.stopAttractLoop();
    applyLayoutState(layout, 'inClaimAnimation');

    fillCoin.props({
      visible: false,
    });

    // pig needs to swap to front layer when animation starts.
    pigContainer?.parent?.addChild(pigContainer);

    this.updateCoinsClaimedAmount(coinReward);

    await this.runGlowSequence();

    await Promise.all([this.runShatterSequence(), this.showReward(250)]);

    coinCounter.tweenTo({ alpha: 1 }, 300, 'quadOut', true);

    let remainingClaimCoins = coinReward;
    await animateCoinFlySequence({
      startPoint: { x: coinSprite.x, y: coinSprite.y },
      targetPoint: coinParticleSlot.toLocal(coinCounter.getIconGlobalPosition()),
      particleParent: coinParticleSlot,
      coinCount: coinReward,
      unitsPerParticle: coinReward / 60,
      totalDurationCapMs: 1000,
      onCoinFlyStart: (coinsFlown: number) => {
        remainingClaimCoins = Math.max(0, remainingClaimCoins - coinsFlown);
        this.updateCoinsClaimedAmount(remainingClaimCoins);
      },
      onCoinArrived: (coinsFlown: number) => {
        coinCounter.updateEnabled = true;
        coinCounter.updateAmount(coinCounter.getDisplayedAmount() + coinsFlown);
        coinCounter.updateEnabled = false;
      },
      isCancelled: () => root.destroyed,
    });

    await this.hideReward();
  }

  private async showReward(delay: number): Promise<void> {
    const { layout, particleLayer } = this;

    layout.rewardContainer.props({
      visible: true,
      alpha: 0,
    });

    this.sparkleEmitters.forEach((emitter) => {
      emitter.spawnActive = false;
    });

    particleLayer.addParticleEmitter(0, 0, rewardParticlesJSON as FXParticleEmitterConfig);

    await Promise.all([
      layout.rewardContainer
        .tweenTo(
          {
            alpha: 1,
          },
          250,
          'quadOut',
          true,
          delay,
        )
        .asyncOnComplete(),
      layout.coinsAmountText
        .tweenFrom(
          {
            scaleX: 0.5,
            scaleY: 0.5,
          },
          600,
          'backOut',
          true,
          delay,
        )
        .asyncOnComplete(),
      layout.coinSprite
        .tweenFrom(
          {
            scaleX: 0.5,
            scaleY: 0.5,
          },
          600,
          'backOut',
          true,
          delay,
        )
        .asyncOnComplete(),
    ]);
  }

  private async hideReward(): Promise<void> {
    const { layout } = this;

    await Promise.all([
      layout.rewardContainer
        .tweenTo(
          {
            alpha: 0,
          },
          300,
          'quadOut',
          true,
        )
        .asyncOnComplete(),
      layout.coinsAmountText
        .tweenTo(
          {
            scaleX: 0.5,
            scaleY: 0.5,
          },
          300,
          'backOut',
          true,
        )
        .asyncOnComplete(),
      layout.coinSprite
        .tweenTo(
          {
            scaleX: 0.5,
            scaleY: 0.5,
          },
          300,
          'backOut',
          true,
        )
        .asyncOnComplete(),
    ]);
  }

  private async runGlowSequence(): Promise<void> {
    const { layout } = this;
    const {
      pigLayout: { pigContainer, pigCracks, pigPieces, pigGlowSprite },
    } = layout;
    const cycleDuration = 700;
    const cycles = 2;

    pigContainer.props({
      visible: true,
      angle: 0,
      alpha: 1,
    });

    pigPieces.props({
      visible: true,
      alpha: 0,
    });

    pigGlowSprite.props({
      visible: true,
      alpha: 0,
      scale: 0.9,
    });

    pigCracks.props({
      visible: true,
      alpha: 0,
    });

    await Promise.all([
      (async (): Promise<void> => {
        let angle = 2;
        for (let i = 0; i < cycles * 4; i += 1) {
          await pigContainer
            .tweenTo(
              {
                angle,
              },
              cycleDuration / 4,
              'sinInOut',
              true,
              1,
              0,
              true,
            )
            .asyncOnComplete();
          angle *= -0.8;
        }
      })(),
      pigContainer
        .tweenTo(
          {
            scaleX: 1.1,
            scaleY: 1.1,
          },
          cycleDuration,
          'backOut',
          true,
          undefined,
          cycles,
          true,
        )
        .asyncOnComplete(),
      pigGlowSprite
        .tweenTo(
          {
            alpha: 0.85,
            scaleX: 1.05,
            scaleY: 1.05,
          },
          cycleDuration,
          'backOut',
          true,
          undefined,
          cycles,
          true,
        )
        .asyncOnComplete(),
      pigCracks
        .tweenTo(
          {
            alpha: 0.85,
            scaleX: 1.05,
            scaleY: 1.05,
          },
          cycleDuration,
          'backOut',
          true,
          undefined,
          cycles,
          true,
        )
        .asyncOnComplete(),
      pigPieces
        .tweenTo(
          {
            alpha: 1,
          },
          cycleDuration,
          'backOut',
          true,
          undefined,
          cycles,
          true,
        )
        .asyncOnComplete(),
    ]);

    await pigGlowSprite
      .tweenTo(
        {
          alpha: 0,
        },
        100,
        'quadOut',
        true,
      )
      .asyncOnComplete();
  }

  private async runShatterSequence(): Promise<void> {
    const {
      pigLayout: { pigSprite, pigFullSprite, pigPieces, pigShadow },
      coinsEmpty,
      coinsFull,
      raysContainer,
    } = this.layout;

    const minParticleSpeed = 0.65;
    const maxParticleSpeed = 1;
    const particleVelocity: PointData = { x: 0, y: 0 };
    const duration = 500;

    pigSprite.props({
      visible: false,
    });

    pigFullSprite.props({
      visible: false,
    });

    pigShadow.props({
      visible: false,
    });

    pigPieces.props({
      visible: true,
      alpha: 1,
    });

    raysContainer.props({
      mask: null,
    });

    await Promise.all([
      ...this.pieces.map((shard) => {
        if (shard.destroyed || !(shard instanceof Sprite)) return Promise.resolve();

        const particleSpeed = MathUtils.getRandomValueInRange(minParticleSpeed, maxParticleSpeed);
        MathUtils.getUnitVector(shard.x, shard.y, particleVelocity);
        particleVelocity.x *= particleSpeed;
        particleVelocity.y *= particleSpeed;

        turnIntoParticle(shard, {
          velocity: {
            ...particleVelocity,
            drag: 0.95,
          },
          gravity: 0.0,
          angularDrag: 0.99,
          angularVelocity: MathUtils.getRandomValueInRange(-0.02, 0.02),
        });

        return shard
          .tweenTo(
            {
              alpha: 0,
            },
            duration,
            'sinIn',
            true,
            Math.random() * 50,
          )
          .asyncOnComplete();
      }),
      ...coinsEmpty.children.map((coin) => coin.tweenTo({ alpha: 0 }, duration, 'sinIn', true).asyncOnComplete()),
      ...coinsFull.children.map((coin) => coin.tweenTo({ alpha: 0 }, duration, 'sinIn', true).asyncOnComplete()),
      animateColorTo(pigPieces, 0xffffff, 0, 3, duration, 'sinIn'),
    ]);
  }

  private async injectPrice(): Promise<void> {
    const { claimButtonText } = this.layout;

    const priceLabel = await this.config.getPiggyBankPrice();
    if (this.destroyed || priceLabel === null) return;
    claimButtonText.text = priceLabel;
  }

  public async toggleClaimButton(enabled: boolean, isFull: boolean): Promise<void> {
    const {
      layout,
      config: { isLastChance, piggyBankState },
    } = this;
    const {
      loadingSpinnerSlot,
      claimButton: { root: claimButton },
    } = this.layout;

    const hasTiers = piggyBankState.currentTier !== PiggyBankTier.Legacy;

    let claimableState: string;
    if (!hasTiers) {
      claimableState = 'claimable';
    } else if (isLastChance) {
      claimableState = 'claimable_lastChance';
    } else {
      claimableState = 'claimable_tier';
    }

    let lockedState: string;
    if (isFull) {
      lockedState = 'inProgressAnimation';
    } else if (hasTiers) {
      lockedState = 'unclaimable_tier';
    } else {
      lockedState = 'unclaimable';
    }

    if (enabled) {
      revokeLayoutState(layout, lockedState);
      applyLayoutState(layout, claimableState);

      claimButton.visible = true;
      const spinner = new LoadingSpinnerView().props({
        parent: loadingSpinnerSlot,
      });
      await this.injectPrice();
      spinner.destroy();

      claimButton.interactive = true;
      claimButton.hitArea = this.claimButtonActiveHitArea;
    } else {
      revokeLayoutState(layout, claimableState);
      applyLayoutState(layout, lockedState);
      claimButton.visible = false;
      claimButton.interactive = false;
      claimButton.hitArea = null;
    }
  }

  public fadeIn(onComplete?: () => void): void {
    this.alpha = 0;
    const tween = this.tweenTo({ alpha: 1 }, 333, 'quadOut', true);
    if (onComplete) tween.onComplete.addOnce(onComplete);
  }

  private async fadeOut(onComplete?: () => void): Promise<void> {
    const tween = this.tweenTo({ alpha: 0 }, 333, 'quadOut', true);
    if (onComplete) tween.onComplete.addOnce(onComplete);
    await tween.asyncOnComplete();
  }

  public resize(width: number, height: number): void {
    const { layout } = this;

    layout.foreground.props({
      x: width / 2,
      y: height / 2,
    });
  }

  public destroy(): void {
    this.onClaimPiggyBank.disconnectAll();
    super.destroy({ children: true });
  }
}
